import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from '../components/UserMenu';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import axios from 'axios';
import {
  getLaboratorios,
  crearLaboratorio,
  actualizarLaboratorio,
  LaboratorioOption,
} from '../api/analisisApi';
import { ApiError } from '../types/auth.types';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import Modal from '../components/Modal';
import styles from './AdminPage.module.css';

type Mode = 'list' | 'create' | 'edit';

export default function LaboratoriosPage() {
  const navigate = useNavigate();
  const [laboratorios, setLaboratorios] = useState<LaboratorioOption[]>([]);
  const [mode, setMode] = useState<Mode>('list');
  const [editing, setEditing] = useState<LaboratorioOption | null>(null);
  const [nombre, setNombre] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLaboratorios()
      .then(setLaboratorios)
      .finally(() => setLoadingData(false));
  }, []);

  function openCreate() {
    setEditing(null);
    setNombre('');
    setError(null);
    setMode('create');
  }

  function openEdit(lab: LaboratorioOption) {
    setEditing(lab);
    setNombre(lab.nombre);
    setError(null);
    setMode('edit');
  }

  function cancel() {
    setMode('list');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) { setError('El nombre es obligatorio.'); return; }
    setError(null);
    setLoading(true);
    try {
      if (mode === 'create') {
        const nuevo = await crearLaboratorio({ nombre: nombre.trim() });
        setLaboratorios((prev) => [...prev, nuevo]);
      } else if (editing) {
        const actualizado = await actualizarLaboratorio(editing.id, { nombre: nombre.trim() });
        setLaboratorios((prev) => prev.map((l) => (l.id === actualizado.id ? actualizado : l)));
      }
      setMode('list');
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al guardar.');
      } else {
        setError('Error al guardar.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>← Volver</button>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Laboratorios</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        {mode === 'list' && (
          <div className={styles.listActions}>
            <button className={styles.newBtnContent} onClick={openCreate}>+ Nuevo laboratorio</button>
          </div>
        )}
        {/* ── Formulario ── */}
        <Modal
          isOpen={mode !== 'list'}
          onClose={cancel}
          title={mode === 'create' ? 'Nuevo laboratorio' : 'Editar laboratorio'}
          width={440}
          disableClose={loading}
        >
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="nombre">Nombre</label>
              <input
                id="nombre"
                className={styles.input}
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre del laboratorio"
                autoFocus
              />
            </div>
            <div className={styles.formActions}>
              <Button type="submit" loading={loading} loadingText="Guardando...">Guardar</Button>
              <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={loading}>Cancelar</button>
            </div>
          </form>
        </Modal>

        {/* ── Lista ── */}
        {loadingData ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando laboratorios...</p>
          </div>
        ) : laboratorios.length === 0 ? (
          <p className={styles.emptyMsg}>No hay laboratorios registrados.</p>
        ) : (
          <div className={styles.listCard}>
            {laboratorios.map((lab, idx) => (
              <div key={lab.id} className={`${styles.listRow} ${idx % 2 === 0 ? styles.listRowEven : ''}`}>
                <span className={styles.listName}>{lab.nombre}</span>
                <button className={styles.editBtn} onClick={() => openEdit(lab)}>Editar</button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
